import TileLayer from 'ol/layer/Tile';
import { ImageTile, Map } from 'ol';
import { XYZ } from 'ol/source';
import { createXYZ } from 'ol/tilegrid';
import { transformExtent } from 'ol/proj';
import { DTN_API } from 'configs/endpoints';
import { dtnTokenManager } from 'services/dtn/auth';
import { DTNTimestamp, DTNTimestampWrapper } from 'services/dtn/types';
import { createLogger } from 'services/debugLogger';
import { createBlobUrl } from 'utils/blobUrl';
import { playbackCache, storeCachedImage } from 'utils/imageCaches';

const log = createLogger('DTNTileCache');

// DTN tiles go to the shared playback cache: a tileSetId is an immutable model run, so they
// need no TTL and stay available for the whole time-series session (see utils/imageCaches).
const dtnTileCache = playbackCache;

// Cache key strips the token so it stays stable across token refreshes.
function getDTNTileCacheKey(url: string): string {
  try {
    const u = new URL(url);
    u.searchParams.delete('token');
    return u.toString();
  } catch {
    return url;
  }
}

/**
 * Flattens all timestamps from every issued run in the API response.
 * The API returns one wrapper per model run; using only [0] would miss data
 * beyond the first run's valid range.
 */
export function flattenDTNTimestamps(data: DTNTimestampWrapper[] | undefined): DTNTimestamp[] {
  return data?.flatMap((w) => w.timestamps) ?? [];
}

/**
 * Finds the DTNTimestamp whose `valid` time is closest to the given ISO8601 string.
 * Returns null if the list is empty.
 */
export function findClosestDTNTimestamp(
  timestamps: DTNTimestamp[],
  isoTime: string,
): DTNTimestamp | null {
  if (!timestamps.length) return null;
  const target = new Date(isoTime).getTime();
  return timestamps.reduce((closest, ts) => {
    const diff = Math.abs(new Date(ts.valid).getTime() - target);
    const closestDiff = Math.abs(new Date(closest.valid).getTime() - target);
    return diff < closestDiff ? ts : closest;
  });
}

/**
 * Creates an OpenLayers XYZ source for a DTN tile layer.
 * Uses URL template so OpenLayers handles tile-coordinate substitution natively.
 * Token is baked into the URL at source-creation time; the source must be
 * recreated when the token refreshes (handled by updateDTNTileSetId on time change).
 */
export function createDTNTileSource({
  layerId,
  tileSetId,
  filterIds,
}: {
  layerId: string;
  tileSetId: string;
  filterIds?: string[];
}) {
  const token = dtnTokenManager.getCachedToken();
  const filterParam = filterIds?.length ? `&filter=${filterIds.join(',')}` : '';
  const source = new XYZ({
    url: `${DTN_API}/tiles/${layerId}/${tileSetId}/{z}/{x}/{y}?token=${token}${filterParam}`,
    crossOrigin: 'anonymous',
    interpolate: false,
    tileSize: 256,
    maxZoom: 12,
    tileLoadFunction: async (tile, src) => {
      if (!(tile instanceof ImageTile)) return;
      const cacheKey = getDTNTileCacheKey(src);
      const cached = dtnTileCache.get(cacheKey);
      if (cached) {
        log.debug('cache hit', { cacheKey, size: dtnTileCache.size });
        const image = tile.getImage() as HTMLImageElement;
        image.src = cached.url;
        return;
      }
      log.debug('cache miss', { cacheKey, size: dtnTileCache.size });
      try {
        const resp = await fetch(src, { mode: 'cors', credentials: 'omit' });
        if (!resp.ok) return;
        const blob = await resp.blob();
        // Another request (tile loader or prewarm) may have filled the entry meanwhile;
        // reuse it instead of overwriting it and orphaning its blob URL.
        const existing = dtnTileCache.get(cacheKey);
        const blobUrl = existing?.url ?? createBlobUrl(blob);
        const image = tile.getImage() as HTMLImageElement;
        image.src = blobUrl;
        if (!existing) storeCachedImage(dtnTileCache, cacheKey, { url: blobUrl, size: blob.size });
      } catch {
        // tile load failure — tile remains blank
      }
    },
  });

  // Store identifiers so they can be read when updating the layer
  source.set('provider', 'dtn');
  source.set('layerId', layerId);
  source.set('tileSetId', tileSetId);
  source.set('filterIds', filterIds ?? []);

  return source;
}

/**
 * Creates a full OpenLayers TileLayer backed by the DTN XYZ source.
 */
export function createDTNTileLayer({
  layerId,
  tileSetId,
  opacity = 0.8,
}: {
  layerId: string;
  tileSetId: string;
  opacity?: number;
}) {
  return new TileLayer({
    opacity,
    source: createDTNTileSource({ layerId, tileSetId }),
  });
}

/**
 * Swaps the tileSetId on an existing DTN TileLayer without recreating the layer.
 * Use this when the selected time changes.
 */
export function updateDTNTileSetId(
  layer: TileLayer<XYZ>,
  tileSetId: string,
) {
  const current = layer.getSource();
  const layerId = current?.get('layerId') as string;
  if (!layerId) return;
  const filterIds = current?.get('filterIds') as string[] | undefined;
  const newSource = createDTNTileSource({ layerId, tileSetId, filterIds });
  layer.setSource(newSource);
}

/**
 * Warms the browser HTTP cache for the given tileSetIds by fetching tiles
 * for the current viewport directly via fetch(). This bypasses OL's rendering
 * pipeline so it works regardless of layer opacity or render-cycle state.
 *
 * @param onProgress - called with a 0–1 ratio as tiles complete
 */
export async function prewarmDTNTiles(
  layerId: string,
  tileSetIds: string[],
  map: Map,
  onProgress?: (ratio: number) => void,
  filterIds?: string[],
): Promise<void> {
  if (!tileSetIds.length) return;

  const token = await dtnTokenManager.getToken();
  const filterParam = filterIds?.length ? `&filter=${filterIds.join(',')}` : '';
  const view = map.getView();
  const size = map.getSize();
  if (!size) return;

  const zoom = view.getZoom();
  if (zoom === undefined) return;

  const z = Math.min(Math.floor(zoom), 12);

  // Transform the viewport extent to EPSG:3857 for the XYZ tile grid
  const extent3857 = transformExtent(
    view.calculateExtent(size),
    view.getProjection(),
    'EPSG:3857',
  );

  const tileGrid = createXYZ({ maxZoom: 12, tileSize: 256 });
  const tileRange = tileGrid.getTileRangeForExtentAndZ(extent3857, z);

  const urls: string[] = tileSetIds.flatMap((tileSetId) => {
    const rows = [];
    for (let x = tileRange.minX; x <= tileRange.maxX; x += 1) {
      rows.push(
        ...Array.from(
          { length: tileRange.maxY - tileRange.minY + 1 },
          (_, i) => `${DTN_API}/tiles/${layerId}/${tileSetId}/${z}/${x}/${tileRange.minY + i}?token=${token}${filterParam}`,
        ),
      );
    }
    return rows;
  });

  if (!urls.length) return;

  log.debug('prewarm start', { layerId, tileSetCount: tileSetIds.length, totalTiles: urls.length });
  let completed = 0;
  await Promise.all(
    urls.map(async (url) => {
      try {
        const cacheKey = getDTNTileCacheKey(url);
        if (!dtnTileCache.has(cacheKey)) {
          const resp = await fetch(url, { mode: 'cors', credentials: 'omit' });
          if (resp.ok) {
            const blob = await resp.blob();
            if (!dtnTileCache.has(cacheKey)) {
              storeCachedImage(dtnTileCache, cacheKey, {
                url: createBlobUrl(blob),
                size: blob.size,
              });
            }
          }
        }
      } catch {
        // ignore individual tile fetch errors
      } finally {
        completed += 1;
        onProgress?.(completed / urls.length);
      }
    }),
  );
  log.debug('prewarm complete', { layerId, cacheSize: dtnTileCache.size, maxSize: dtnTileCache.max });
}
